import { convertNumericColorToHex, numericColorBrightness } from 'app/infrastructure/universal/color';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import {
  ChartSeriesDataOptionsBuilder
} from './chart-series-data-options-builder';
import { SeriesTypeColumnMappings, SeriesTypeBaseIndex } from './types';

interface SynergiHighchartsDataPoint extends Highcharts.PointOptionsObject {
  [key: string]: any;
  synDataRowIndex?: number;
  seriesType: string;
}

export class ChartPointDataOptionsBuilder {
  private dataAdapter: FavouriteDataAdapter;
  private seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder;

  constructor(
    dataAdapter: FavouriteDataAdapter,
    seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder
  ) {
    this.dataAdapter = dataAdapter;
    this.seriesDataOptionsBuilder = seriesDataOptionsBuilder;
  }

  public createDataPoints(
    options: Highcharts.Options,
    splitCol: number,
    seriesCol: number,
    seriesValues: FavouriteRowValue[],
    seriesTypeBaseIndex: SeriesTypeBaseIndex,
    columnMap: SeriesTypeColumnMappings,
    xIndex: number
  ) {
    const ignoreNullValues = this.ignoreNullValuesForDataPoints(xIndex);

    this.dataAdapter.dataModel.rows.forEach((row, index) => {
      if ((xIndex >= 0) && (row[xIndex].value == null) && ignoreNullValues) {
        return;
      }

      this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex);
      if (this.dataAdapter.hasRollingAverage() && columnMap.rollingAverageSeries) {
        this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex, 'rollingAverageSeries');
      }
      if (this.dataAdapter.hasTarget() && columnMap.targetSeries) {
        this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex, 'targetSeries');
      }
    });
  }

  private createDataPoint(
    options: Highcharts.Options,
    rowIndex: number,
    seriesCol: number,
    seriesValues: FavouriteRowValue[],
    row: FavouriteRowValue[],
    seriesTypeBaseIndex: SeriesTypeBaseIndex,
    columnMap: SeriesTypeColumnMappings,
    xIndex: number,
    seriesType: string = 'mainSeries'
  ) {
    let seriesIndex = seriesCol >= 0 ? seriesValues.findIndex(series => { return series.value === row[seriesCol].value; }) : 0;
    seriesIndex += seriesTypeBaseIndex[seriesType];
    const dataArray = (options.series[seriesIndex] as any).data;
    const dataPoint: SynergiHighchartsDataPoint = { seriesType: seriesType };
    for (let c = 0; c < columnMap[seriesType].length; c++) {
      const colType = columnMap[seriesType][c];
      if (colType === 'splitting' || colType === 'series' || !colType) {
        continue;
      }
      const rowVal = row[c];
      let val: any = rowVal.value;
      if ((colType === 'x' || colType === 'y') && this.dataAdapter.hasCategories(c)) {
        val = this.dataAdapter.columnCategories[c].findIndex(cat => { return val === cat.value; });
      }
      if (this.seriesDataOptionsBuilder.shouldAddColorToDataPoint(seriesCol) && rowVal.bgColour) {
        dataPoint.color = convertNumericColorToHex(rowVal.bgColour);
        if (this.seriesDataOptionsBuilder.shouldAdjustDataPointLabelColor() && numericColorBrightness(rowVal.bgColour) < 128) {
          dataPoint.dataLabels = {
            ...(dataPoint.dataLabels || {}),
            color: '#ffffff'
          };
        }
      }
      dataPoint[columnMap[seriesType][c]] = val;
    }

    this.addStaticDataPointProperties(dataPoint);

    if (xIndex >= 0) {
      dataPoint.name = this.dataAdapter.format(xIndex, row[xIndex].value);
    }

    // We set our own row index property to easily identify which row in the data a point belongs to (used for drilldown)
    dataPoint.synDataRowIndex = rowIndex;

    (dataArray as Highcharts.PointOptionsObject[]).push(dataPoint);
  }

  public ignoreNullValuesForDataPoints(axisColIndex: number): boolean {
    return !this.dataAdapter.hasCategories(axisColIndex);
  }

  public addStaticDataPointProperties(dataPoint: Highcharts.PointOptionsObject) {
    return;
  }
}
